import { useFunnel } from "@airhelp/funnel";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { useTranslation } from "react-i18next";
import { ControlledAirportAutocomplete } from "../../../modules/form/ControlledAirportsAutocomplete";
import { Flex } from "@chakra-ui/react";
import { AirplaneLanding, AirplaneTakeOff, Info } from "@airhelp/icons";
import {
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import { InfoBox } from "@airhelp/react";
import TranslationWithHTML from "../../../../components/ui-elements/translation-with-html/TranslationWithHTML";
import { useForm, useFormHandlers } from "../../../modules/form";
import { z } from "zod";
import {
  updateItineraryFlightByIndex,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { useAppSettingsSnapshot } from "v2/settings";
import { isRtl } from "utils/getLocaleDirection";

export const airportSchema = z.object({
  iata: z.string(),
  name: z.string(),
  city: z.string(),
  rank: z.number().optional(),
  ec261: z.boolean().optional(),
  country: z.string().optional(),
  timeZone: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  searchText: z.string().optional(),
  countryCode: z.string().optional(),
  cityTranslations: z.record(z.string().optional()).optional(),
});

const defaultStepSchema = z.object({
  departureAirport: airportSchema.nullable(),
  arrivalAirport: airportSchema.nullable(),
});

const stepSchema = z.object({
  departureAirport: airportSchema,
  arrivalAirport: airportSchema,
});

export function FlightDetailsStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { itineraryFlights } = usePreEligibilityDataSnapshot();
  const { locale } = useAppSettingsSnapshot();

  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting, isDirty },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      departureAirport: itineraryFlights[0].departureAirport,
      arrivalAirport: itineraryFlights[itineraryFlights.length - 1].arrivalAirport,
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    onSubmit({ arrivalAirport, departureAirport }) {
      if (isDirty) {
        updateItineraryFlightByIndex(0, {
          departureAirport,
        });

        updateItineraryFlightByIndex(itineraryFlights.length - 1, {
          arrivalAirport,
        });
      }

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();
      funnel.stepForward();
    },
  });
  const arrivalAirport = watch("arrivalAirport");
  const departureAirport = watch("departureAirport");

  return (
    <FunnelStepContainer data-testid="flightDetailsStep">
      <FunnelStepSection title={t("steps.flight_details.text")}>
        <Flex
          flexDir={{ base: "column", md: "row" }}
          justifyContent={{ base: "flex-start", md: "space-between" }}
          data-sentry-unmask
        >
          <ControlledAirportAutocomplete
            control={control}
            dataTestId="departureAirportAutocomplete"
            name={"departureAirport"}
            label={t("components.airline_autocomplete.departed_label")}
            placeholder={t("components.airline_autocomplete.departed_placeholder")}
            formControlStyles={{
              mb: { base: 6, md: 0 },
              maxWidth: { base: "100%", md: "calc(50% - 8px)" },
            }}
            icon={<AirplaneTakeOff boxSize={6} />}
            isOptionDisabled={(option) => option.value.iata === arrivalAirport?.iata}
            isRtl={isRtl(locale)}
          />
          <ControlledAirportAutocomplete
            control={control}
            name="arrivalAirport"
            dataTestId="arrivalAirportAutocomplete"
            label={t("components.airline_autocomplete.arrival_label")}
            placeholder={t("components.airline_autocomplete.arrival_placeholder")}
            formControlStyles={{
              maxWidth: { base: "100%", md: "calc(50% - 8px)" },
            }}
            icon={<AirplaneLanding boxSize={6} />}
            isOptionDisabled={(option) => option.value.iata === departureAirport?.iata}
            isRtl={isRtl(locale)}
          />
        </Flex>
      </FunnelStepSection>

      <InfoBox isChat icon={<Info boxSize={5} color="primary.500" />}>
        <TranslationWithHTML text={t("steps.flight_details.hint")} />
      </InfoBox>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
